import {
  type CreateKnowledgeArticle,
  type KnowledgeFolderNode,
  KnowledgeSlugSchema,
  type KnowledgeVisibility,
} from '@sbc/domain-models/knowledge-base';
import { type FormEvent, type ReactNode, useState } from 'react';

import { MarkdownEditor } from '@/components/markdown';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAdminUsers } from '@/lib/api/users';

import { slugify } from './slugify';
import { StringListInput } from './string-list-input';

export interface ArticleFormValues {
  title: string;
  slug: string;
  folderId: string;
  visibility: KnowledgeVisibility;
  ownerId: string;
  questions: string[];
  tags: string[];
  body: string;
}

export function emptyArticleValues(folderId: string): ArticleFormValues {
  return {
    title: '',
    slug: '',
    folderId,
    visibility: 'customer',
    ownerId: '',
    questions: [],
    tags: [],
    body: '',
  };
}

// The create/update request bodies share this exact shape; both the new and edit
// pages build it from the form values, so keep the mapping in one place.
export function toArticlePayload(values: ArticleFormValues): CreateKnowledgeArticle {
  return {
    folderId: values.folderId,
    slug: values.slug,
    title: values.title,
    questions: values.questions,
    tags: values.tags,
    visibility: values.visibility,
    body: values.body,
    ownerId: values.ownerId,
  };
}

const slugIsValid = (slug: string) => KnowledgeSlugSchema.safeParse(slug).success;

export function ArticleForm({
  initial,
  folders,
  submitting,
  submitLabel,
  error,
  header,
  footer,
  onSubmit,
  onCancel,
}: {
  initial: ArticleFormValues;
  folders: KnowledgeFolderNode[];
  submitting: boolean;
  submitLabel: string;
  error?: string | null;
  // Slotted extras: the approved-edit warning + status above, the approve/archive
  // actions + checklist below.
  header?: ReactNode;
  footer?: ReactNode;
  onSubmit: (values: ArticleFormValues) => void;
  onCancel: () => void;
}) {
  const usersQuery = useAdminUsers();
  const [values, setValues] = useState<ArticleFormValues>(initial);
  // Once the user edits the slug (or on an existing article), stop auto-suggesting.
  const [slugTouched, setSlugTouched] = useState(initial.slug !== '');

  const sortedFolders = [...folders].toSorted((a, b) => a.path.localeCompare(b.path));
  const owners = usersQuery.data ?? [];
  const effectiveOwnerId = values.ownerId || (owners[0]?.id ?? '');

  const set = <K extends keyof ArticleFormValues>(key: K, value: ArticleFormValues[K]) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const onTitleChange = (title: string) => {
    setValues((prev) => ({
      ...prev,
      title,
      slug: slugTouched ? prev.slug : slugify(title),
    }));
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    onSubmit({
      ...values,
      ownerId: effectiveOwnerId,
      title: values.title.trim(),
      questions: values.questions.map((q) => q.trim()).filter(Boolean),
      tags: values.tags.map((t) => t.trim()).filter(Boolean),
    });
  };

  const canSubmit =
    !submitting &&
    values.title.trim() !== '' &&
    values.folderId !== '' &&
    effectiveOwnerId !== '' &&
    slugIsValid(values.slug);

  return (
    <form className="space-y-6" onSubmit={handleSubmit}>
      {header}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="title">Title</Label>
          <Input
            id="title"
            required
            placeholder="Dehydrated skin: what helps and what to use"
            value={values.title}
            onChange={(event) => onTitleChange(event.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="slug">Slug</Label>
          <Input
            id="slug"
            required
            placeholder="dehydrated-skin"
            value={values.slug}
            onChange={(event) => {
              setSlugTouched(true);
              set('slug', event.target.value);
            }}
          />
          {values.slug !== '' && !slugIsValid(values.slug) && (
            <p className="text-xs text-[var(--color-destructive)]">
              Must be kebab-case (lowercase letters, digits, single hyphens).
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="folderId">Folder</Label>
          <Select value={values.folderId} onValueChange={(value) => set('folderId', value)}>
            <SelectTrigger id="folderId">
              <SelectValue placeholder="Select a folder…" />
            </SelectTrigger>
            <SelectContent>
              {sortedFolders.map((folder) => (
                <SelectItem key={folder.id} value={folder.id}>
                  {folder.path}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="visibility">Visibility</Label>
          <Select
            value={values.visibility}
            onValueChange={(value) =>
              set('visibility', value === 'internal' ? 'internal' : 'customer')
            }
          >
            <SelectTrigger id="visibility">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="customer">customer</SelectItem>
              <SelectItem value="internal">internal</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="ownerId">Owner</Label>
          <Select
            value={effectiveOwnerId}
            disabled={usersQuery.isPending}
            onValueChange={(value) => set('ownerId', value)}
          >
            <SelectTrigger id="ownerId">
              <SelectValue placeholder="Select an owner…" />
            </SelectTrigger>
            <SelectContent>
              {owners.map((owner) => (
                <SelectItem key={owner.id} value={owner.id}>
                  {owner.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Questions</Label>
        <p className="text-xs text-[var(--color-muted-foreground)]">
          Customer questions this article answers, phrased the way customers ask.
        </p>
        <StringListInput
          values={values.questions}
          onChange={(next) => set('questions', next)}
          placeholder="How do I know if my skin is dehydrated?"
          addLabel="Add question"
        />
      </div>

      <div className="space-y-2">
        <Label>Tags</Label>
        <p className="text-xs text-[var(--color-muted-foreground)]">
          Keywords, synonyms, and common misspellings.
        </p>
        <StringListInput
          values={values.tags}
          onChange={(next) => set('tags', next)}
          placeholder="dehydrated"
          addLabel="Add tag"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="body">Body (markdown)</Label>
        <MarkdownEditor
          id="body"
          required={false}
          value={values.body}
          onChange={(next) => set('body', next)}
        />
      </div>

      {error && <p className="text-sm text-[var(--color-destructive)]">{error}</p>}

      <div className="flex items-center gap-2">
        <Button type="submit" disabled={!canSubmit}>
          {submitting ? 'Saving…' : submitLabel}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>

      {footer}
    </form>
  );
}
